/**
 * PATH: packages/ui/src/components/email/EmailRow.tsx
 * FILE_ID: UI-EMAIL-ROW-001
 * PURPOSE: The universal inbox/folder row — a thin composer over the
 *          row/* sub-modules. Renders ONE uniform flat row for every email:
 *          avatar (with the AI status pip + category dot riding its corners),
 *          a clean single identity column (sender · subject · preview), a
 *          single classification chip run (one category pill + one Board>Lane
 *          work chip — which, when the AI link is pending, carries its OWN
 *          always-visible ✓/✗) on its OWN full-width line, and — BELOW the
 *          body — a hover-revealed (desktop) / always-on (touch) DISPOSITION bar
 *          (star · bin · junk). Allocated rows get a thin left colour-accent
 *          rail (board/category hue) instead of a heavy frame.
 * WHY:     Operator (FR-0638): "the tags are all over … the ux is kinda
 *          broken". 2026-06-21 (with screenshot): the chips spilled over the
 *          always-visible action cluster ("Over✓✗heads & ★Renewals"), the
 *          category pill wrapped to two lines, and the left gutter stacked
 *          three ragged circles (checkbox · AI dot · avatar). v4: the action
 *          cluster moves to its own bar BELOW the body ("on hover you see an
 *          action below the card") so the chip line is never crushed/spilled;
 *          the AI pip rides the avatar corner so the gutter is one anchor.
 *          v5 (2026-06-22, "confirm board should only be on hover on the
 *          arrow"): the AI review ✓/✗ leaves the bottom bar for the work chip
 *          it refers to, so the bar is pure disposition and the row reads calmer.
 *          v5.1 (2026-06-22, "where did the tick and cross go… can't read the
 *          lanes"): v5.0 hid them behind a chevron and crowded the lane — now
 *          the ✓/✗ are ALWAYS VISIBLE on the chip, right after the lane.
 *          v5.2 (2026-06-22, screenshot "fix this … fix the expansion tray"):
 *          a lone reject ✗ floated bare-red in the chip seam (read as an error)
 *          and the action tray stranded 3 icons under a full-width hairline.
 *          Fix: the ✓/✗ ride a faint capsule attached to the work chip (calm
 *          reject), and the tray is a contained left-aligned action capsule.
 * FLOW:
 *   ┌────────────────────┐   ┌──────────────────┐   ┌──────────────────────┐
 *   │ email + props      │ → │ resolveRowAccent │ → │ pip · identity · meta │
 *   │ (host-resolved)    │   │ (pure)           │   │ · actions (uniform)   │
 *   └────────────────────┘   └──────────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - row/*: rowTypes, rowGrammar, resolveRowAccent, RowIdentity, RowMeta,
 *     RowWorkChip, RowActions — the decomposed pieces this assembles.
 *   - react-router-dom: deduped peer dep (job/board deep links).
 * RELATED:
 *   - email-hub InboxPage.tsx / FolderEmailRow.tsx: the hosts that wire props.
 */
import { Building2, CheckSquare, Square } from 'lucide-react'

import { cn } from '../../lib/utils'
import { resolveRowAccent } from './row/resolveRowAccent'
import { ROW_BODY, ROW_BODY_COMPACT, ROW_HEADER, ROW_ROOT, ROW_SECONDARY } from './row/rowGrammar'
import { RowActions } from './row/RowActions'
import { RowIdentity } from './row/RowIdentity'
import { RowMeta } from './row/RowMeta'
import { RowWorkChip } from './row/RowWorkChip'
import type { EmailRowProps } from './row/rowTypes'

export type { EmailRowProps, EmailRowCategoryConfig } from './row/rowTypes'

export function EmailRow(props: EmailRowProps) {
  const {
    email,
    category,
    isSelected,
    isChecked,
    anySelected = false,
    onSelect,
    onToggleSelect,
    onToggleStar,
    onContextMenu,
    onArchive,
    isArchiving = false,
    onUnarchive,
    isUnarchiving = false,
    onBin,
    isBinning = false,
    onRestore,
    isRestoring = false,
    onToggleRead,
    isTogglingRead = false,
    categoryChip,
    allocationChip,
    allocationFrame,
    allocateChip,
    suggestedJobChip,
    mailboxChip,
    relationChip,
    roleChip,
    jobPillClass,
    onRejectJob,
    isRejectingJob = false,
    onConfirmJob,
    isConfirmingJob = false,
    onConfirmAllocation,
    onRejectAllocation,
    isAllocationBusy = false,
    onConfirmSuggestion,
    onRejectSuggestion,
    isSuggestionBusy = false,
    onFilterSender,
    onFilterCompany,
    companyLabel,
    highlightQuery,
    aiTag,
    visibilityBadge,
    junkControl,
    selectable = true,
    compact = false,
    forceActionsExpanded = false,
    peerName,
    peerAddress,
    peerPrefix,
  } = props

  const accent = resolveRowAccent({
    allocationFrame,
    category,
    email,
    onConfirmJob,
    onRejectJob,
    isConfirmingJob,
    isRejectingJob,
    onConfirmAllocation,
    onRejectAllocation,
    isAllocationBusy,
    onConfirmSuggestion,
    onRejectSuggestion,
    isSuggestionBusy,
  })

  // Background tint: selection wins, then unread leans blue, else a quiet
  // hover. Uniform across allocated + unallocated (the left rail carries the
  // workflow colour, not the whole card).
  const bgClass = isSelected
    ? 'bg-primary/[0.08]'
    : !email.is_read
      ? 'bg-blue-50/40 dark:bg-blue-950/20 hover:bg-accent/40'
      : 'hover:bg-accent/30'

  // The work chip (Board>Lane for allocated rows, or the linked-job pill).
  // Only present it when there's genuinely work to show, so RowMeta's
  // empty-state guard can collapse a quiet row's chip line.
  const hasWork = accent.isAllocated || !!email.job_id
  const workChip = hasWork ? (
    <RowWorkChip
      accent={accent}
      allocationFrame={allocationFrame}
      email={email}
      allocationChip={allocationChip}
      jobPillClass={jobPillClass}
      onConfirmJob={onConfirmJob}
      isConfirmingJob={isConfirmingJob}
      onRejectJob={onRejectJob}
      isRejectingJob={isRejectingJob}
      // v5.1: the framed AI review (board/suggested/job) rides the chip as an
      // always-visible ✓/✗ — inert on unframed rows (resolveRowAccent gates it).
      review={accent.review}
      forceExpanded={forceActionsExpanded}
    />
  ) : null

  // "Add to board" only when there's genuinely no work yet — an allocated /
  // job-linked / suggested row already shows its work chip, so it never also
  // shows the allocate affordance (one workflow control per row).
  const allocateNode = !hasWork ? allocateChip : null

  // Sender-company filter chip — moved OFF the identity line (where it crushed
  // the name on a narrow split column) onto the bottom scrolling strip. One
  // click filters to the whole company; demoted (ROW_SECONDARY) so it leans
  // quiet until hover.
  const companyChip =
    onFilterCompany && companyLabel ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onFilterCompany()
        }}
        title={`Show all emails from ${companyLabel}`}
        className={cn(
          ROW_SECONDARY,
          // text-[10px] to sit on the uniform chip floor (was text-[9px] — the
          // odd sub-10px straggler in the strip, 2026-06-22 declutter).
          'shrink-0 gap-1 px-2 py-0.5 text-[10px] rounded-full font-medium max-w-[160px] bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
      >
        <Building2 className="h-2.5 w-2.5 shrink-0" />
        <span className="truncate">{companyLabel}</span>
      </button>
    ) : null

  return (
    <div
      // Stamp the email id on the root so InboxPage can resolve "what is the
      // operator hovering" via event delegation (the `c`-to-archive hotkey).
      data-email-id={email.id}
      onClick={(event) => {
        if (event.shiftKey) {
          event.preventDefault()
          onToggleSelect(event as unknown as React.MouseEvent<HTMLButtonElement>)
          return
        }
        onSelect(event)
      }}
      onContextMenu={onContextMenu}
      className={cn(ROW_ROOT, bgClass)}
    >
      {/* Left accent rail — the thin colour signal that replaces the heavy
          card frame. Allocated rows wear the board/category hue; a selected
          unallocated row gets a primary rail; everything else stays clean. */}
      {accent.isAllocated ? (
        <span
          aria-hidden
          data-testid="board-frame-accent"
          data-frame-color={accent.accentColor}
          className="pointer-events-none absolute inset-y-0 left-0 w-[3px] z-[1]"
          style={{ backgroundColor: accent.accentColor }}
        />
      ) : isSelected ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-[3px] z-[1] bg-primary"
        />
      ) : null}

      <div className={compact ? ROW_BODY_COMPACT : ROW_BODY} data-density={compact ? 'compact' : 'expanded'}>
        {/* Identity HEADER row — checkbox · avatar · sender/subject/preview.
            The chip run + disposition bar are NO LONGER nested in the identity
            text column (RowIdentity's footer slot); they live in the full-width
            footer below so they align to the checkbox, not the avatar. */}
        <div className={ROW_HEADER}>
          {selectable && (
            <button
              onClick={onToggleSelect}
              aria-label={isChecked ? 'Deselect email' : 'Select email for bulk actions'}
              aria-pressed={isChecked}
              className={cn(
                'mt-0.5 p-0.5 rounded transition-opacity shrink-0 group-hover:opacity-100',
                isChecked || anySelected ? 'opacity-100' : 'opacity-40',
              )}
            >
              {isChecked ? (
                <CheckSquare className="h-4 w-4 text-primary" />
              ) : (
                <Square className="h-4 w-4 text-muted-foreground/60" />
              )}
            </button>
          )}

          {/* The AI status pip rides the avatar's corner now (passed into
              RowIdentity), not a separate leading column — one avatar-anchored
              gutter, not three stacked circles with ragged margins. */}
          <RowIdentity
            email={email}
            category={category}
            highlightQuery={highlightQuery}
            onFilterSender={onFilterSender}
            peerName={peerName}
            peerAddress={peerAddress}
            peerPrefix={peerPrefix}
            aiTag={aiTag}
          />
        </div>

        {/* FULL-WIDTH FOOTER — lifted OUT of the identity text column so the
            chip run + disposition bar start at the CHECKBOX's left edge (the
            row's leftmost side) instead of indenting under the avatar
            (operator 2026-06-23, with screenshot: "the alignment … system
            starting where the check box is at the left most side … same with
            the star"). As siblings of ROW_HEADER inside ROW_BODY's px-3, they
            share the checkbox's left margin and span the full row width. */}

        {/* Chip strip — full width: the category + Board>Lane chips own the
           whole row, so a long board name can't be spilled over by a trailing
           cluster (BUG-2026-06-21-EMAIL-ROW-CHIP-SPILL). */}
        <div className="mt-1.5">
          <RowMeta
            email={email}
            categoryChip={categoryChip}
            workChip={workChip}
            allocateChip={allocateNode}
            suggestedJobChip={suggestedJobChip}
            visibilityBadge={visibilityBadge}
            relationChip={relationChip}
            roleChip={roleChip}
            companyChip={companyChip}
            mailboxChip={mailboxChip}
          />
        </div>

        {/* Action bar — the disposition cluster (star · bin · junk), in a row
           of its OWN BELOW the body (operator 2026-06-21: "on hover you see an
           action below the card"). v4 settled the placement; v5 (2026-06-22)
           moved the AI review onto the work chip so this bar is pure
           disposition; v5.2 dropped the full-width hairline in favour of a
           self-contained LEFT-aligned action capsule (see RowActions). The
           reveal keeps a 2px rise synced with the fade so it slides up into
           place. The bar is collapsed at rest on hover-capable devices
           (grid-rows 0fr→1fr on row hover, never overlaying the text) and
           ALWAYS open on touch (you can't hover a phone). Inbox passes
           forceActionsExpanded for the top visible row so that card stays
           fully open without hover (#452). */}
        <div
          data-testid="row-action-bar"
          className={cn(
            'grid overflow-hidden transition-[grid-template-rows,opacity,margin-top] duration-200 ease-out',
            'grid-rows-[1fr] opacity-100 mt-2',
            !forceActionsExpanded &&
              '[@media(hover:hover)]:grid-rows-[0fr] [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:mt-0',
            !forceActionsExpanded &&
              '[@media(hover:hover)]:group-hover:grid-rows-[1fr] [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-hover:mt-2',
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={cn(
                // v6: the hairline is BACK — but it now sits above a LABELLED
                // toolbar (star · bin · junk verbs) that fills the footer, so it
                // reads as a deliberate divider, not the "stark border-t over
                // empty space" that v5.2 stripped when the bar was three
                // stranded glyphs. The border lives in the overflow-hidden
                // region so it's clipped at rest and only reveals on hover with
                // the slide-up. forceActionsExpanded skips the hover slide so
                // the divider stays visible at rest on the pinned top row.
                'border-t border-border/40 pt-2',
                'transition-transform duration-200 ease-out',
                !forceActionsExpanded &&
                  '[@media(hover:hover)]:translate-y-1 [@media(hover:hover)]:group-hover:translate-y-0',
              )}
            >
              <RowActions
                isStarred={email.is_starred}
                onToggleStar={onToggleStar}
                isRead={email.is_read}
                onToggleRead={onToggleRead}
                isTogglingRead={isTogglingRead}
                onBin={onBin}
                isBinning={isBinning}
                onRestore={onRestore}
                isRestoring={isRestoring}
                onArchive={onArchive}
                isArchiving={isArchiving}
                onUnarchive={onUnarchive}
                isUnarchiving={isUnarchiving}
                junkControl={junkControl}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
